import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState, AppDispatch } from "../redux/store";
import {
  Typography,
  Container,
  Box,
  IconButton,
  Button,
  TextField,
  ImageListItem,
  Tooltip,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableFooter,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  FormControl,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { format } from "date-fns";
import { getProduct, updateProduct } from "../redux/slices/productSlice";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import { productNameTransform } from "../constants/Products";
import {
  CoverageItem,
  FaqItem,
  FeaturesItem,
  ProductDetail,
} from "../interfaces/product-interface";
import useDetectSession from "../hooks/useDetectSession.hook";
import {
  inputStyle,
  pageHeading,
  primaryBtnStyle,
  primaryBtnStyleOutline,
} from "../styles/styling";
import AppContainer from "../components/AppContainer";
import { PermissionsByDocKey } from "../interfaces/permissions-interface";
import { transformArrToObj } from "../utils/transform-permissions-obj";
import LoadingOverlay from "../components/LoadingOverlay";

interface RenderStringArrayProps {
  arrObj: string[];
  itemTableLabel: string;
  isEditMode: boolean;
  onAddItem: Function;
  addItemLabel: string;
  onRemoveItem: Function;
  onEditItem: Function;
  inputLabel: string;
}

const RenderStringArray = ({
  arrObj,
  itemTableLabel,
  isEditMode,
  onAddItem,
  addItemLabel,
  onRemoveItem,
  onEditItem,
  inputLabel,
}: RenderStringArrayProps) => {
  return (
    <TableContainer
      component={Paper}
      sx={{
        "& th": {
          backgroundColor: "#E1E1E1",
          borderBlockStyle: "solid",
          borderWidth: 1,
        },
      }}
    >
      <Table>
        <TableHead>
          <TableRow>
            <TableCell colSpan={3}>
              <Typography variant="body1" textAlign={"center"}>
                {itemTableLabel}
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {arrObj.map((detailItem: string, detailIndex: number) => (
            <TableRow key={detailIndex}>
              <TableCell>
                <TextField
                  value={detailItem}
                  onChange={(e) => {
                    onEditItem(e, detailIndex);
                  }}
                  fullWidth
                  placeholder={`Enter ${inputLabel}`}
                  label={inputLabel}
                  required
                  disabled={!isEditMode}
                  sx={inputStyle()}
                />
              </TableCell>
              {isEditMode && (
                <TableCell>
                  <IconButton
                    onClick={() => {
                      onRemoveItem(detailIndex);
                    }}
                  >
                    <RemoveCircleOutlineIcon />
                  </IconButton>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell
              onClick={() => {
                onAddItem();
              }}
            >
              {isEditMode && (
                <IconButton
                  onClick={() => {
                    onAddItem();
                  }}
                >
                  <AddCircleOutlineIcon />
                  <Typography paddingLeft={5} variant="h6">
                    {addItemLabel}
                  </Typography>
                </IconButton>
              )}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

const ViewProductPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  const { productName } = location.state;
  const { status, error } = useSelector((state: RootState) => state.products);
  const currentUser = useSelector((state: RootState) => state.auth.currentUser);

  const [currentUserPermissions, setCurrentUserPermissions] =
    useState<PermissionsByDocKey>();

  const idToken = localStorage.getItem("idToken");
  useDetectSession();

  const [product, setProduct] = useState<ProductDetail>();
  const [editedProduct, setEditedProduct] = useState<ProductDetail>();
  const [isEditMode, setIsEditMode] = useState(false);

  const [benefitsArray, setBenefitsArray] = useState<CoverageItem[]>([]);
  const [editBenefitsArray, setEditBenefitsArray] = useState<CoverageItem[]>(
    []
  );
  const [exclusionsArray, setExclusionsArray] = useState<CoverageItem[]>([]);
  const [editExclusionsArray, setEditExclusionsArray] = useState<
    CoverageItem[]
  >([]);

  const [qualificationDesc, setQualificationDesc] = useState<string>("");
  const [qualificationsArray, setQualificationsArray] = useState<string[]>([]);
  const [qualificationsArrayRef, setQualificationsArrayRef] = useState<
    string[]
  >([]);

  const [expiryDate, setExpiryDate] = useState<Date | null>(
    new Date(format(new Date(), "MM/dd/yyyy kk:mm:ss"))
  );

  const [editFeatures, setEditFeatures] = useState<FeaturesItem[]>([]);
  const [showFeaturesOpts, setShowFeaturesOpts] = useState<boolean>(false);
  const [selectedFeaturesOption, setSelectedFeaturesOption] = useState<
    "titleAndDesc" | "titleAndDescWithDetails"
  >("titleAndDesc");
  const [editFaqs, setEditFaqs] = useState<FaqItem[]>([]);
  const [showFaqOptions, setShowFaqOptions] = useState<boolean>(false);
  const [selectedFaqOption, setSelectedFaqOption] = useState<
    "qAndA" | "qAndAnsArr" | "qAndAnsWithDetails"
  >("qAndA");

  const handleSaveClick = () => {
    if (!editedProduct) {
      return;
    }

    let coverage = { ...product?.coverage };

    if (editBenefitsArray) {
      coverage.benefits = editBenefitsArray;
    }
    if (editExclusionsArray) {
      coverage.exclusion = editExclusionsArray;
    }
    if (editedProduct?.tagline) {
      coverage.productDescription = editedProduct.tagline;
    }

    const transformedName =
      productNameTransform[product?.name as keyof typeof productNameTransform];

    dispatch(
      updateProduct({
        name: transformedName,
        data: {
          ...editedProduct,
          coverage,
          applicationQualification:
            typeof editedProduct?.applicationQualification !== "string"
              ? {
                  description: qualificationDesc ?? "",
                  enumerated: qualificationsArray,
                }
              : qualificationDesc,
          updatedBy: currentUser?.fullname ?? "",
        },
      })
    ).then(() => {
      dispatch(getProduct(transformedName)).then((response: any) => {
        const product: ProductDetail = response.payload;
        setProduct(product);
        setEditedProduct(product);
        setBenefitsArray(product?.coverage?.benefits ?? []);
        setEditBenefitsArray(product?.coverage?.benefits ?? []);
        setExclusionsArray(product?.coverage?.exclusion ?? []);
        setEditExclusionsArray(product?.coverage?.exclusion ?? []);

        if (typeof product?.applicationQualification !== "string") {
          setQualificationsArray(
            product?.applicationQualification?.enumerated ?? []
          );
          setQualificationsArrayRef(
            product?.applicationQualification?.enumerated ?? []
          );
          setQualificationDesc(
            product?.applicationQualification?.description ?? ""
          );
        } else {
          setQualificationDesc(product?.applicationQualification ?? "");
        }

        setEditFeatures(product?.features ?? []);
        setExpiryDate(
          product?.expiryDate ? new Date(product?.expiryDate) : null
        );
        setEditFaqs(product?.faq ?? []);
      });
    });
    setIsEditMode(false);
  };

  const handleTextFieldChange = (field: string, value: string) => {
    if (
      field === "applicationQualification.description" ||
      field === "applicationQualification"
    ) {
      setQualificationDesc(value);
    }
    if (editedProduct) {
      setEditedProduct({ ...editedProduct, [field]: value });
    }
  };

  const updateProductPayload = (field: string, value: any) => {
    if (editedProduct) {
      setEditedProduct({ ...editedProduct, [field]: value });
    }
  };

  const returnArray = (checkObject: any, objectArray?: any[]) => {
    return typeof checkObject !== "string" && objectArray ? objectArray : [];
  };

  const onCancelChanges = () => {
    setEditBenefitsArray(benefitsArray);
    setEditExclusionsArray(exclusionsArray);
    setIsEditMode(false);
    if (typeof product?.applicationQualification !== "string") {
      setQualificationsArray(
        returnArray(
          product?.applicationQualification,
          product?.applicationQualification?.enumerated
        )
      );
      setQualificationDesc(
        product?.applicationQualification?.description ?? ""
      );
      setQualificationsArrayRef(
        product?.applicationQualification?.enumerated ?? []
      );
    } else {
      setQualificationsArray([]);
      setQualificationsArrayRef([]);
      setQualificationDesc(product?.applicationQualification);
    }
    setExpiryDate(product?.expiryDate ? new Date(product?.expiryDate) : null);
    setEditFeatures(product?.features ?? []);
    setEditFaqs(product?.faq ?? []);
  };

  useEffect(() => {
    if (!idToken) {
      navigate("/login");
      return;
    }
  }, [dispatch, idToken, navigate]);

  useEffect(() => {
    if (currentUser && currentUser.userPermissions) {
      setCurrentUserPermissions(transformArrToObj(currentUser.userPermissions));
    }
  }, [currentUser]);

  useEffect(() => {
    const transformedName =
      productNameTransform[productName as keyof typeof productNameTransform];

    dispatch(getProduct(transformedName)).then((response: any) => {
      const product: ProductDetail = response.payload;
      setProduct(product);
      setEditedProduct(product);
      setBenefitsArray(product?.coverage?.benefits ?? []);
      setEditBenefitsArray(product?.coverage?.benefits ?? []);
      setExclusionsArray(product?.coverage?.exclusion ?? []);
      setEditExclusionsArray(product?.coverage?.exclusion ?? []);

      if (typeof product?.applicationQualification !== "string") {
        setQualificationsArray(
          product?.applicationQualification?.enumerated ?? []
        );
        setQualificationsArrayRef(
          product?.applicationQualification?.enumerated ?? []
        );
        setQualificationDesc(
          product?.applicationQualification?.description ?? ""
        );
      } else {
        setQualificationDesc(product?.applicationQualification ?? "");
      }

      setEditFeatures(product?.features ?? []);
      setExpiryDate(product?.expiryDate ? new Date(product?.expiryDate) : null);
      setEditFaqs(product?.faq ?? []);
    });
  }, [dispatch, productName]);

  useEffect(() => {
    if (editFeatures) {
      updateProductPayload("features", editFeatures);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editFeatures]);

  useEffect(() => {
    if (editFaqs) {
      updateProductPayload("faq", editFaqs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editFaqs]);

  const handleBackClick = () => {
    onCancelChanges();
    navigate(-1);
  };

  const renderCoverageItem = (
    item: CoverageItem,
    index: number,
    setEditedArray: Function,
    editedArray: CoverageItem[]
  ) => {
    return !isEditMode ? (
      <TableRow key={item.title}>
        <TableCell>{item.title}</TableCell>
        <TableCell>{item.description}</TableCell>
      </TableRow>
    ) : (
      <TableRow key={index}>
        <TableCell>
          <TextField
            value={editedArray[index].title}
            onChange={(e) => {
              const newVal = {
                title: e.target.value,
                description: item.description,
              };
              setEditedArray(
                editedArray.map((newItem, currentIndex) => {
                  if (currentIndex === index) {
                    return newVal;
                  }
                  return newItem;
                })
              );
            }}
            fullWidth
            placeholder="Title"
            sx={inputStyle()}
          />
        </TableCell>
        <TableCell>
          <TextField
            value={editedArray[index].description}
            onChange={(e) => {
              const newVal = {
                description: e.target.value,
                title: item.title,
              };
              setEditedArray(
                editedArray.map((newItem, currentIndex) => {
                  if (currentIndex === index) {
                    return newVal;
                  }
                  return newItem;
                })
              );
            }}
            fullWidth
            placeholder="Description"
            sx={inputStyle()}
          />
        </TableCell>
        <TableCell>
          <IconButton
            onClick={() => {
              setEditedArray(
                editedArray.filter((_, itemIndex) => itemIndex !== index)
              );
            }}
          >
            <RemoveCircleOutlineIcon />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  };

  const renderQualificationsItem = (item: string, index: number) => {
    return isEditMode ? (
      <TableRow key={item}>
        <TableCell colSpan={2}>
          <TextField
            value={qualificationsArray[index]}
            onChange={(e) => {
              setQualificationsArray(
                qualificationsArray.map((item, currentIndex) => {
                  if (currentIndex === index) {
                    return e.target.value;
                  }
                  return item;
                })
              );
            }}
            fullWidth
            placeholder="Qualifications Item"
            sx={inputStyle()}
          />
        </TableCell>
        <TableCell colSpan={1}>
          <IconButton
            onClick={() => {
              setQualificationsArrayRef(
                qualificationsArrayRef.filter(
                  (_, itemIndex) => itemIndex !== index
                )
              );
              setQualificationsArray(
                qualificationsArrayRef.filter(
                  (_, itemIndex) => itemIndex !== index
                )
              );
            }}
          >
            <RemoveCircleOutlineIcon />
          </IconButton>
        </TableCell>
      </TableRow>
    ) : (
      <TableRow key={item}>
        <TableCell colSpan={3}>{item}</TableCell>
      </TableRow>
    );
  };

  const renderFeaturesContentItem = (
    item:
      | string
      | {
          description?: string;
          enumerated?: string[];
        },
    index: number
  ) => {
    if (typeof item === "string") {
      return (
        <TextField
          value={item}
          onChange={(e) => {
            const updatedFeature = editFeatures.map((feat, featIndex) => {
              if (featIndex !== index) {
                return feat;
              } else {
                return { title: feat.title, content: e.target.value };
              }
            });
            setEditFeatures(updatedFeature);
          }}
          fullWidth
          placeholder="Feature Description"
          label="Feature Description"
          required
          disabled={!isEditMode}
          sx={[inputStyle(), { marginTop: "15px" }]}
        />
      );
    } else {
      return (
        <>
          <TextField
            value={item?.description}
            onChange={(e) => {
              const updatedFeature = editFeatures.map((feat, featIndex) => {
                if (featIndex !== index) {
                  return feat;
                } else if (
                  featIndex === index &&
                  typeof feat.content !== "string"
                ) {
                  return {
                    title: feat.title,
                    content: {
                      description: e.target.value,
                      enumerated: feat.content?.enumerated,
                    },
                  };
                } else {
                  return feat;
                }
              });
              setEditFeatures(updatedFeature);
            }}
            fullWidth
            placeholder="Feature Description"
            label="Feature Description"
            required
            disabled={!isEditMode}
            sx={[inputStyle(), { marginTop: "15px", marginBottom: "15px" }]}
          />
          <RenderStringArray
            arrObj={item?.enumerated ?? []}
            itemTableLabel="Feature Details"
            inputLabel="Feature Detail"
            isEditMode={isEditMode}
            onAddItem={() => {
              const updatedFeature = editFeatures.map((feat, featIndex) => {
                if (featIndex !== index) {
                  return feat;
                } else if (
                  featIndex === index &&
                  typeof feat.content !== "string"
                ) {
                  return {
                    title: feat.title,
                    content: {
                      description: feat.content?.description,
                      enumerated: [...(feat.content?.enumerated ?? []), ""],
                    },
                  };
                } else {
                  return feat;
                }
              });
              setEditFeatures(updatedFeature);
            }}
            addItemLabel="Add Feature Details"
            onEditItem={(e: any, detailIndex: any) => {
              const updatedFeature = editFeatures.map((feat, featIndex) => {
                if (featIndex !== index) {
                  return feat;
                } else if (
                  featIndex === index &&
                  typeof feat.content !== "string"
                ) {
                  const updatedDetail = feat.content?.enumerated?.map(
                    (enumItem: string, enumIndex: number) => {
                      if (enumIndex === detailIndex) {
                        return e.target.value;
                      } else {
                        return enumItem;
                      }
                    }
                  );
                  return {
                    title: feat.title,
                    content: {
                      description: feat.content?.description,
                      enumerated: updatedDetail,
                    },
                  };
                } else {
                  return feat;
                }
              });
              setEditFeatures(updatedFeature);
            }}
            onRemoveItem={(detailIndex: any) => {
              const updatedFeature = editFeatures.map((feat, featIndex) => {
                if (featIndex !== index) {
                  return feat;
                } else if (
                  featIndex === index &&
                  typeof feat.content !== "string"
                ) {
                  const updatedDetail = feat.content?.enumerated?.filter(
                    (_, enumIndex: number) => enumIndex !== detailIndex
                  );
                  return {
                    title: feat.title,
                    content: {
                      description: feat.content?.description,
                      enumerated: updatedDetail,
                    },
                  };
                } else {
                  return feat;
                }
              });
              setEditFeatures(updatedFeature);
            }}
          />
        </>
      );
    }
  };

  const renderFeatures = (item: FeaturesItem, index: number) => {
    return (
      <Box
        style={{ borderRadius: 10, borderWidth: 1, margin: 20, width: "100%" }}
      >
        <TextField
          value={item.title}
          onChange={(e) => {
            const updatedFeature = editFeatures.map((feat, featIndex) => {
              if (featIndex !== index) {
                return feat;
              } else {
                return { title: e.target.value, content: feat.content };
              }
            });
            setEditFeatures(updatedFeature);
          }}
          fullWidth
          placeholder="Feature Title"
          label="Feature Title"
          required
          disabled={!isEditMode}
          sx={inputStyle()}
        />
        {renderFeaturesContentItem(item.content, index)}
        {isEditMode && (
          <Button
            variant="outlined"
            startIcon={<DeleteIcon />}
            color="error"
            style={{ marginTop: 15 }}
            onClick={() => {
              setEditFeatures(
                editFeatures.filter((_, featureIndex) => featureIndex !== index)
              );
            }}
          >
            Delete
          </Button>
        )}
      </Box>
    );
  };

  const renderFaqs = (item: FaqItem, index: number) => {
    if (typeof item.answer === "string") {
      // Answer is a string
      return (
        <Box
          style={{
            borderRadius: 10,
            borderWidth: 1,
            margin: 20,
            width: "100%",
          }}
        >
          <TextField
            value={item.question}
            onChange={(e) => {
              const updatedFaq = editFaqs.map((faq, faqIndex) => {
                if (faqIndex !== index) {
                  return faq;
                } else {
                  return { question: e.target.value, answer: faq.answer };
                }
              });
              setEditFaqs(updatedFaq);
            }}
            fullWidth
            placeholder="Question"
            label="Question"
            required
            disabled={!isEditMode}
            sx={inputStyle()}
          />
          <TextField
            value={item.answer}
            onChange={(e) => {
              const updatedFaq = editFaqs.map((faq, faqIndex) => {
                if (faqIndex !== index) {
                  return faq;
                } else {
                  return { answer: e.target.value, question: faq.question };
                }
              });
              setEditFaqs(updatedFaq);
            }}
            fullWidth
            placeholder="Answer"
            label="Answer"
            required
            disabled={!isEditMode}
            sx={[inputStyle(), { marginTop: "15px" }]}
          />
          {isEditMode && (
            <Button
              variant="outlined"
              startIcon={<DeleteIcon />}
              color="error"
              style={{ marginTop: 15 }}
              onClick={() => {
                setEditFaqs(
                  editFaqs.filter((_, faqIndex) => faqIndex !== index)
                );
              }}
            >
              Delete
            </Button>
          )}
        </Box>
      );
    } else if (
      Array.isArray(item.answer) &&
      item.answer.every((ansType) => typeof ansType === "string")
    ) {
      // Answer is an array of strings
      return (
        <Box
          style={{
            borderRadius: 10,
            borderWidth: 1,
            margin: 20,
            width: "100%",
          }}
        >
          <TextField
            value={item.question}
            onChange={(e) => {
              const updatedFaq = editFaqs.map((faq, faqIndex) => {
                if (faqIndex !== index) {
                  return faq;
                } else {
                  return { question: e.target.value, answer: faq.answer };
                }
              });
              setEditFaqs(updatedFaq);
            }}
            fullWidth
            placeholder="Question"
            label="Question"
            required
            disabled={!isEditMode}
            sx={[inputStyle(), { marginBottom: "15px" }]}
          />
          <RenderStringArray
            arrObj={item.answer}
            itemTableLabel="Answer"
            addItemLabel="Add Answer"
            inputLabel="Answer"
            isEditMode={isEditMode}
            onAddItem={() => {
              const updatedFaqs = editFaqs.map((faqItem, faqIndex) => {
                if (faqIndex === index && Array.isArray(faqItem.answer)) {
                  return {
                    question: faqItem.question,
                    answer: [...faqItem.answer, ""],
                  };
                } else {
                  return faqItem;
                }
              });
              setEditFaqs(updatedFaqs);
            }}
            onRemoveItem={(faqAnsIndex: any) => {
              const filterAnswers = Array.isArray(item.answer)
                ? item.answer.filter(
                    (_, ansItemIndex) => ansItemIndex !== faqAnsIndex
                  )
                : [];
              const updatedFaqs = editFaqs.map((faqItem, faqIndex) => {
                if (faqIndex === index && Array.isArray(faqItem.answer)) {
                  return {
                    question: faqItem.question,
                    answer: filterAnswers,
                  };
                } else {
                  return faqItem;
                }
              });
              setEditFaqs(updatedFaqs);
            }}
            onEditItem={(e: any, faqAnsIndex: any) => {
              const filterAnswers = Array.isArray(item.answer)
                ? item.answer.map((ansItem, ansItemIndex) => {
                    if (ansItemIndex !== faqAnsIndex) {
                      return ansItem;
                    } else {
                      return e.target.value;
                    }
                  })
                : [];
              const updatedFaqs = editFaqs.map((faqItem, faqIndex) => {
                if (faqIndex === index && Array.isArray(faqItem.answer)) {
                  return {
                    question: faqItem.question,
                    answer: filterAnswers,
                  };
                } else {
                  return faqItem;
                }
              });
              setEditFaqs(updatedFaqs);
            }}
          />
          {isEditMode && (
            <Button
              variant="outlined"
              startIcon={<DeleteIcon />}
              color="error"
              style={{ marginTop: 15 }}
              onClick={() => {
                setEditFaqs(
                  editFaqs.filter((_, faqIndex) => faqIndex !== index)
                );
              }}
            >
              Delete
            </Button>
          )}
        </Box>
      );
    } else if (
      typeof item.answer === "object" &&
      !Array.isArray(item.answer) &&
      Array.isArray(item.answer.enumerated) &&
      item.answer.enumerated.every(
        (ansItem: any) => typeof ansItem === "string"
      )
    ) {
      // Answer is in form: {description: string, enumerated: string[]}
      return (
        <Box
          style={{
            borderRadius: 10,
            borderWidth: 1,
            margin: 20,
            width: "100%",
          }}
        >
          <TextField
            value={item.question}
            onChange={(e) => {
              const updatedFaq = editFaqs.map((faq, faqIndex) => {
                if (faqIndex !== index) {
                  return faq;
                } else {
                  return { question: e.target.value, answer: faq.answer };
                }
              });
              setEditFaqs(updatedFaq);
            }}
            fullWidth
            placeholder="Question"
            label="Question"
            required
            disabled={!isEditMode}
            sx={[inputStyle(), { marginBottom: "15px" }]}
          />
          <TextField
            value={item.answer.description}
            onChange={(e) => {
              const updatedFaq = editFaqs.map((faq, faqIndex) => {
                if (faqIndex !== index) {
                  return faq;
                } else if (
                  typeof faq.answer !== "string" &&
                  !Array.isArray(faq.answer)
                ) {
                  return {
                    question: faq.question,
                    answer: {
                      description: e.target.value,
                      enumerated: faq.answer.enumerated,
                    },
                  };
                } else {
                  return faq;
                }
              });
              setEditFaqs(updatedFaq);
            }}
            fullWidth
            placeholder="Answer"
            label="Answer"
            required
            disabled={!isEditMode}
            sx={[inputStyle(), { marginBottom: "15px" }]}
          />
          <RenderStringArray
            arrObj={item.answer.enumerated}
            itemTableLabel="Answer Details"
            inputLabel="Answer Detail"
            addItemLabel="Add Answer Detail"
            isEditMode={isEditMode}
            onAddItem={() => {
              const updatedFaqs = editFaqs.map((faqItem, faqIndex) => {
                if (
                  faqIndex === index &&
                  !Array.isArray(faqItem.answer) &&
                  typeof faqItem.answer !== "string"
                ) {
                  return {
                    question: faqItem.question,
                    answer: {
                      description: faqItem.answer.description,
                      enumerated: [...faqItem.answer.enumerated, ""],
                    },
                  };
                } else {
                  return faqItem;
                }
              });
              setEditFaqs(updatedFaqs);
            }}
            onRemoveItem={(faqAnsIndex: any) => {
              const filterAnswers =
                typeof item.answer === "object" &&
                Array.isArray(item.answer.enumerated)
                  ? item.answer.enumerated.filter(
                      (_: any, ansItemIndex: number) =>
                        ansItemIndex !== faqAnsIndex
                    )
                  : [];
              const updatedFaqs = editFaqs.map((faqItem, faqIndex) => {
                if (faqIndex === index && Array.isArray(faqItem.answer)) {
                  return {
                    question: faqItem.question,
                    answer: filterAnswers,
                  };
                } else {
                  return faqItem;
                }
              });
              setEditFaqs(updatedFaqs);
            }}
            onEditItem={(e: any, faqAnsIndex: any) => {
              const filterAnswers =
                typeof item.answer === "object" &&
                Array.isArray(item.answer.enumerated)
                  ? item.answer.enumerated.map(
                      (ansItem: any, ansItemIndex: number) => {
                        if (ansItemIndex !== faqAnsIndex) {
                          return ansItem;
                        } else {
                          return e.target.value;
                        }
                      }
                    )
                  : [];
              const updatedFaqs = editFaqs.map((faqItem, faqIndex) => {
                if (faqIndex === index) {
                  return {
                    question: faqItem.question,
                    answer: {
                      description: faqItem.answer.description,
                      enumerated: filterAnswers,
                    },
                  };
                } else {
                  return faqItem;
                }
              });
              setEditFaqs(updatedFaqs);
            }}
          />
          {isEditMode && (
            <Button
              variant="outlined"
              startIcon={<DeleteIcon />}
              color="error"
              style={{ marginTop: 15 }}
              onClick={() => {
                setEditFaqs(
                  editFaqs.filter((_, faqIndex) => faqIndex !== index)
                );
              }}
            >
              Delete
            </Button>
          )}
        </Box>
      );
    } else {
      // Answer is in form: {description: string, enumerated: {title: string, content: string}[]}
      return <Box />;
    }
  };

  if (error) {
    return (
      <AppContainer>
        <Container sx={{ marginLeft: "240px", flexGrow: 1, padding: 2 }}>
          <Typography variant="h6" color="error">
            Error: {error}
          </Typography>
        </Container>
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      <Box sx={{ flexGrow: 1, padding: 2 }}>
        <Box display="flex" alignItems="center" marginBottom={2}>
          <IconButton onClick={handleBackClick}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={pageHeading}>
            Products Details | {product?.name}
          </Typography>
          {currentUserPermissions?.productsList?.permissions?.edit && (
            <Button
              style={{ position: "absolute", right: 15 }}
              variant="contained"
              onClick={() => {
                setIsEditMode(true);
              }}
              disabled={isEditMode}
              sx={primaryBtnStyle}
            >
              Edit
            </Button>
          )}
        </Box>

        <LoadingOverlay loading={status === "loading"} />

        {/* Product Image */}
        <Box
          display="flex"
          alignItems="center"
          margin={2}
          flexDirection={"column"}
        >
          <Tooltip
            title={`Product Image | ${productName}`}
            placement="right"
            style={{ alignItems: "center", justifyContent: "center" }}
          >
            <ImageListItem
              key={productName}
              style={{ width: "50%", height: "auto" }}
            >
              <img
                srcSet={product?.image}
                src={product?.image}
                alt={productName}
                loading="lazy"
                style={{ objectFit: "cover" }}
              />
            </ImageListItem>
          </Tooltip>
          {isEditMode && (
            <TextField
              label="Enter Product Image URL"
              value={editedProduct?.image}
              onChange={(e) => {
                handleTextFieldChange("image", e.target.value);
              }}
              fullWidth
              margin="dense"
              disabled={!isEditMode}
              required
              sx={[inputStyle(), { marginTop: "20px" }]}
            />
          )}
        </Box>

        {/* Tagline */}
        <Box
          display="flex"
          alignItems="center"
          margin={2}
          flexDirection={"column"}
        >
          <Typography variant="h6">Tagline</Typography>
          <TextField
            label={isEditMode ? "Enter Tagline" : "Tagline"}
            value={editedProduct?.tagline}
            onChange={(e) => {
              handleTextFieldChange("tagline", e.target.value);
            }}
            fullWidth
            margin="dense"
            disabled={!isEditMode}
            required
            sx={inputStyle()}
          />
        </Box>

        {/* Product Expiry */}
        <Box
          display="flex"
          alignItems="center"
          margin={2}
          flexDirection={"column"}
        >
          <Typography variant="h6">Product Expiry</Typography>
          <Box style={{ marginTop: 15 }} width={"50%"}>
            <DateTimePicker
              label="Set Product Expiry Date"
              format="MM/dd/yyyy hh:mm:ss"
              disabled={!isEditMode}
              minDateTime={new Date()}
              value={expiryDate}
              onChange={(newValue) => {
                setExpiryDate(newValue);
                handleTextFieldChange(
                  "expiryDate",
                  (newValue && format(newValue, "MM/dd/yyyy kk:mm:ss")) ?? ""
                );
              }}
              sx={{ width: "100%" }}
            />
          </Box>
        </Box>

        {/* Product Coverage */}
        <Box
          display="flex"
          alignItems="center"
          margin={2}
          flexDirection={"column"}
        >
          <Typography variant="h6">Coverage</Typography>
          <TableContainer
            component={Paper}
            sx={{
              "& th": {
                backgroundColor: "#E1E1E1",
                borderBlockStyle: "solid",
                borderWidth: 1,
              },
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="body1" textAlign={"center"}>
                      Top Inclusions
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {editBenefitsArray?.map((item: CoverageItem, index) =>
                  renderCoverageItem(
                    item,
                    index,
                    setEditBenefitsArray,
                    editBenefitsArray
                  )
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell>
                    {isEditMode && (
                      <IconButton
                        onClick={() => {
                          setEditBenefitsArray((prev) => [
                            ...prev,
                            { title: "", description: "" },
                          ]);
                        }}
                      >
                        <AddCircleOutlineIcon />
                        <Typography paddingLeft={5} variant="h6">
                          Add Top Inclusions
                        </Typography>
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="body1" textAlign={"center"}>
                      Top Exclusions
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {editExclusionsArray?.map((item: CoverageItem, index) =>
                  renderCoverageItem(
                    item,
                    index,
                    setEditExclusionsArray,
                    editExclusionsArray
                  )
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell>
                    {isEditMode && (
                      <IconButton
                        onClick={() => {
                          setEditExclusionsArray((prev) => [
                            ...prev,
                            { title: "", description: "" },
                          ]);
                        }}
                      >
                        <AddCircleOutlineIcon />
                        <Typography paddingLeft={5} variant="h6">
                          Add Top Exclusions
                        </Typography>
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Box>

        {/* Application Qualification */}
        <Box
          display="flex"
          alignItems="center"
          margin={2}
          flexDirection={"column"}
        >
          <Typography variant="h6">Application Qualification</Typography>
          <TextField
            label={isEditMode ? "Enter Qualification" : "Qualification"}
            value={qualificationDesc}
            onChange={(e) => {
              handleTextFieldChange(
                qualificationsArray.length !== 0
                  ? "applicationQualification.description"
                  : "applicationQualification",
                e.target.value
              );
            }}
            fullWidth
            margin="dense"
            disabled={!isEditMode}
            required
            sx={[inputStyle(), { marginBottom: "15px" }]}
          />
          {qualificationsArrayRef.length !== 0 && (
            <TableContainer
              component={Paper}
              sx={{
                "& th": {
                  backgroundColor: "#E1E1E1",
                  borderBlockStyle: "solid",
                  borderWidth: 1,
                },
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Typography variant="body1" textAlign={"center"}>
                        Qualifications List
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {qualificationsArrayRef.map((item: string, index) =>
                    renderQualificationsItem(item, index)
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {isEditMode && (
            <TableContainer component={Paper}>
              <Table>
                <TableFooter
                  style={{ padding: 16, height: 80 }}
                  onClick={() => {
                    setQualificationsArray((prev) => [...prev, ""]);
                    setQualificationsArrayRef((prev) => [...prev, ""]);
                  }}
                >
                  <IconButton style={{ margin: 16 }}>
                    <AddCircleOutlineIcon />
                    <Typography paddingLeft={5} variant="h6">
                      Add Qualifications
                    </Typography>
                  </IconButton>
                </TableFooter>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Features and Benefits */}
        <Box
          display="flex"
          alignItems="center"
          margin={2}
          flexDirection={"column"}
        >
          <Typography variant="h6">Features and Benefits</Typography>
          {editFeatures?.map((featuresItem: FeaturesItem, index: number) =>
            renderFeatures(featuresItem, index)
          )}
          {isEditMode && (
            <TableContainer component={Paper}>
              <Table>
                <TableFooter
                  style={{ padding: 16, height: 80 }}
                  onClick={() => {
                    setShowFeaturesOpts(true);
                  }}
                >
                  <IconButton style={{ margin: 16 }}>
                    <AddCircleOutlineIcon />
                    <Typography paddingLeft={5} variant="h6">
                      Add Features and Benefits
                    </Typography>
                  </IconButton>
                </TableFooter>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Specific Product FAQs */}
        <Box
          display="flex"
          alignItems="center"
          margin={2}
          flexDirection={"column"}
        >
          <Typography variant="h6">FAQs</Typography>
          {editFaqs?.map((faqItem, faqIndex) => renderFaqs(faqItem, faqIndex))}
          {isEditMode && (
            <TableContainer component={Paper}>
              <Table>
                <TableFooter
                  style={{ padding: 16, height: 80 }}
                  onClick={() => {
                    setShowFaqOptions(true);
                  }}
                >
                  <IconButton style={{ margin: 16 }}>
                    <AddCircleOutlineIcon />
                    <Typography paddingLeft={5} variant="h6">
                      Add FAQ
                    </Typography>
                  </IconButton>
                </TableFooter>
              </Table>
            </TableContainer>
          )}
        </Box>

        {isEditMode && (
          <Box
            display="flex"
            margin={2}
            flexDirection={"row"}
            alignItems={"flex-end"}
            justifyContent={"flex-end"}
          >
            <Button
              variant="contained"
              onClick={handleSaveClick}
              color="primary"
              sx={primaryBtnStyle}
            >
              Save
            </Button>
            <Button
              variant="outlined"
              style={{ marginLeft: 15 }}
              onClick={() => {
                onCancelChanges();
              }}
              sx={primaryBtnStyleOutline}
            >
              Cancel
            </Button>
          </Box>
        )}

        {/* FAQ Options */}
        <Dialog
          open={showFaqOptions}
          onClose={() => {
            setShowFaqOptions(false);
          }}
          aria-labelledby="FAQ Option"
        >
          <DialogTitle id="alert-dialog-title">Choose FAQ Format</DialogTitle>
          <DialogContent>
            <FormControl>
              <RadioGroup
                aria-labelledby="faq-options"
                defaultValue={selectedFaqOption}
                name="radio-buttons-group"
              >
                <FormControlLabel
                  value="qAndA"
                  control={<Radio />}
                  label="Question and Answer"
                  onClick={() => setSelectedFaqOption("qAndA")}
                />
                <FormControlLabel
                  value="qAndAnsArr"
                  control={<Radio />}
                  label="Question and Enumerated Answer"
                  onClick={() => setSelectedFaqOption("qAndAnsArr")}
                />
                <FormControlLabel
                  value="qAndAnsWithDetails"
                  control={<Radio />}
                  label="Question and Answer with Description and Details"
                  onClick={() => setSelectedFaqOption("qAndAnsWithDetails")}
                />
              </RadioGroup>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowFaqOptions(false);
                setSelectedFaqOption("qAndA");
              }}
              color="primary"
              sx={[primaryBtnStyleOutline, { width: "60px" }]}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                let newFaqEntry: FaqItem = { question: "", answer: "" };

                if (selectedFaqOption === "qAndAnsArr") {
                  newFaqEntry = {
                    question: "",
                    answer: [""],
                  };
                } else if (selectedFaqOption === "qAndAnsWithDetails") {
                  newFaqEntry = {
                    question: "",
                    answer: {
                      description: "",
                      enumerated: [""],
                    },
                  };
                }

                setEditFaqs([...editFaqs, newFaqEntry]);
                setShowFaqOptions(false);
                setSelectedFaqOption("qAndA");
              }}
              autoFocus
              sx={[primaryBtnStyleOutline, { width: "75px" }]}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* Features and Benefits Options */}
        <Dialog
          open={showFeaturesOpts}
          onClose={() => {
            setShowFeaturesOpts(false);
          }}
          aria-labelledby="Fetures and Benefits Option"
        >
          <DialogTitle id="alert-dialog-title">
            Choose Features and Benefits Format
          </DialogTitle>
          <DialogContent>
            <FormControl>
              <RadioGroup
                aria-labelledby="features-options"
                defaultValue={selectedFeaturesOption}
                name="radio-buttons-group"
              >
                <FormControlLabel
                  value="titleAndDesc"
                  control={<Radio />}
                  label="Title and Description"
                  onClick={() => setSelectedFeaturesOption("titleAndDesc")}
                />
                <FormControlLabel
                  value="titleAndDescWithDetails"
                  control={<Radio />}
                  label="Title and Description with Enumerated Details"
                  onClick={() =>
                    setSelectedFeaturesOption("titleAndDescWithDetails")
                  }
                />
              </RadioGroup>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowFeaturesOpts(false);
                setSelectedFeaturesOption("titleAndDesc");
              }}
              color="primary"
              sx={[primaryBtnStyleOutline, { width: "60px" }]}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                let newFeaturesEntry: FeaturesItem = { title: "", content: "" };

                if (selectedFeaturesOption === "titleAndDesc") {
                  newFeaturesEntry = { title: "", content: "" };
                } else if (
                  selectedFeaturesOption === "titleAndDescWithDetails"
                ) {
                  newFeaturesEntry = {
                    title: "",
                    content: {
                      description: "",
                      enumerated: [""],
                    },
                  };
                }

                setEditFeatures([...editFeatures, newFeaturesEntry]);
                setShowFeaturesOpts(false);
                setSelectedFeaturesOption("titleAndDesc");
              }}
              autoFocus
              sx={[primaryBtnStyleOutline, { width: "75px" }]}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AppContainer>
  );
};

export default ViewProductPage;
